/**
 * JoinPage — `/join/<token>` public route content.
 *
 * Branches on the invite status (from `team.lookupInvite`) and the
 * current session (from `meQueryOptions`):
 *
 *   - status !== 'pending'                    → status card, no actions
 *   - signed in to the inviting workspace     → "you're already a member"
 *   - signed in to a different workspace      → "sign out to continue"
 *   - signed in but no workspace yet          → AccountForm (if needed)
 *                                                + one-click accept
 *   - anonymous                               → phone → OTP → account →
 *                                                accept
 *
 * Per ADR-0011 §3.5, every freshly-signed-in user MUST set name+email
 * before the workspace surface is reachable. The join page enforces
 * this inline (so the invitee never leaves the join URL); the regular
 * sign-in flow uses the standalone `/onboarding/account` route.
 *
 * The pending-invite branches reuse the focused `JoinPhoneStep` /
 * `JoinOtpStep` / shared `AccountForm` components instead of
 * duplicating form plumbing.
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, CardBody } from "@flint/ui";
import { trpc, vanillaTrpc } from "../../lib/trpc.ts";
import { meQueryKey, meQueryOptions, type Me } from "../../lib/session.ts";
import { AccountForm } from "../onboarding/components/index.ts";
import { InviteSummaryCard } from "./invite-summary-card.tsx";
import { JoinPhoneStep } from "./join-phone-step.tsx";
import { JoinOtpStep } from "./join-otp-step.tsx";
import { AlreadySignedInCard } from "./already-signed-in-card.tsx";

export interface JoinPageProps {
  token: string;
}

interface InviteSummary {
  inviteId: string;
  companyId: string;
  workspaceName: string;
  inviterName: string;
  role: "Founder" | "Operator" | "Employee";
  /** Granular role name (ADR-0030). Null for legacy band-only invites. */
  roleName: string | null;
  maskedPhone: string;
  expiresAt: string;
  status: "pending" | "accepted" | "cancelled" | "expired";
}

export function JoinPage({ token }: JoinPageProps) {
  const lookup = useQuery({
    queryKey: ["team", "lookupInvite", token],
    queryFn: () => vanillaTrpc.team.lookupInvite.query({ token }) as Promise<InviteSummary>,
    retry: 1,
  });
  const me = useQuery(meQueryOptions);

  if (lookup.isLoading || me.isLoading) {
    return <p className="text-sm text-muted">Loading invite…</p>;
  }

  if (lookup.error || !lookup.data) {
    return (
      <Alert
        intent="bad"
        title="Invite not found"
        description="The link may be incorrect or expired. Ask your teammate to send a fresh invite."
      />
    );
  }

  const summary = lookup.data;

  return (
    <div className="flex flex-col gap-4 w-full">
      <InviteSummaryCard
        workspaceName={summary.workspaceName}
        inviterName={summary.inviterName}
        role={summary.roleName ?? summary.role}
        maskedPhone={summary.maskedPhone}
        status={summary.status}
      />
      <PendingActions token={token} summary={summary} me={me.data ?? null} />
    </div>
  );
}

interface PendingActionsProps {
  token: string;
  summary: InviteSummary;
  me: Me;
}

function PendingActions({ token, summary, me }: PendingActionsProps) {
  if (summary.status !== "pending") {
    return (
      <Alert
        intent={summary.status === "expired" ? "warn" : "bad"}
        title={
          summary.status === "accepted"
            ? "This invite was already accepted."
            : summary.status === "cancelled"
              ? "This invite was cancelled."
              : "This invite has expired."
        }
        description={
          summary.status === "accepted"
            ? "Sign in with the same phone number from the home page."
            : "Ask your teammate to send a fresh invite."
        }
      />
    );
  }

  // Signed in with a workspace already. The server is the authority
  // (AlreadyMemberOfAnotherWorkspaceError) but failing there yields a
  // useless wire error — do the friendlier thing here.
  if (me?.user && me.company) {
    return (
      <AlreadySignedInCard
        currentUserName={me.user.name ?? "Yourself"}
        currentWorkspaceName={me.company.tradeName ?? me.company.legalName}
        inviteWorkspaceName={summary.workspaceName}
        isSameWorkspace={me.company.id === summary.companyId}
      />
    );
  }

  // Signed in but no workspace yet (post-OTP, pre-create). Skip the
  // OTP step — they're already authed — and go through the
  // account-then-accept flow.
  if (me?.user && !me.company) {
    return <AuthedAcceptFlow token={token} me={me} />;
  }

  // Anonymous — full phone → OTP → account (if needed) → accept flow.
  return <AnonymousJoin token={token} />;
}

// ── Anonymous flow ───────────────────────────────────────────────────

type AnonymousStage =
  | { kind: "phone" }
  | { kind: "otp"; phone: string; challengeId: string }
  | { kind: "verified" };

function AnonymousJoin({ token }: { token: string }) {
  const me = useQuery(meQueryOptions);
  const [stage, setStage] = useState<AnonymousStage>({ kind: "phone" });

  if (stage.kind === "phone") {
    return (
      <JoinPhoneStep
        hint="Enter the same number this invite was sent to. We'll send a 6-digit code via WhatsApp."
        onCodeSent={({ phone, challengeId }) => setStage({ kind: "otp", phone, challengeId })}
      />
    );
  }

  if (stage.kind === "otp") {
    return (
      <JoinOtpStep
        challengeId={stage.challengeId}
        phone={stage.phone}
        onChangePhone={() => setStage({ kind: "phone" })}
        onVerified={() => setStage({ kind: "verified" })}
      />
    );
  }

  // Verified — re-enter the orchestrator with a fresh `me`. The user
  // either has a complete profile (skip account form, go straight to
  // accept) or doesn't (render account form first).
  return <AuthedAcceptFlow token={token} me={me.data ?? null} />;
}

// ── Authed accept flow (account form + accept) ───────────────────────

function AuthedAcceptFlow({ token, me }: { token: string; me: Me }) {
  // Render the account form when the user hasn't filled name+email
  // yet. After it submits + refetches `me`, this component re-renders
  // with `profileComplete === true` and falls through to the accept
  // CTA.
  const profileComplete = me?.user.profileComplete ?? false;

  if (!profileComplete) {
    return (
      <AccountForm
        me={me}
        submitLabel="Save and continue"
        pendingLabel="Saving"
        onComplete={() => {
          // No-op: the AccountForm's own refetch updates `me`, which
          // re-renders this component into the accept-CTA branch.
        }}
      />
    );
  }

  return <AcceptCard token={token} />;
}

function AcceptCard({ token }: { token: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accept = trpc.team.acceptInvite.useMutation({
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: meQueryKey });
      // New members land on the module launcher (ADR-0049) like every
      // other post-auth entry.
      void navigate({ to: "/launch" });
    },
  });

  return (
    <Card>
      <CardBody className="flex flex-col gap-3.5">
        <p className="text-sm text-ink-2">
          You&apos;re verified — tap below to join the workspace.
        </p>
        <Button type="button" onClick={() => accept.mutate({ token })} disabled={accept.isPending}>
          {accept.isPending ? "Joining workspace…" : "Accept invite & join"}
        </Button>
        {accept.error ? (
          <Alert intent="bad" title="Couldn't accept invite" description={accept.error.message} />
        ) : null}
      </CardBody>
    </Card>
  );
}
